import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

// components
import {
  Button,
  Col,
  Divider,
  Flex,
  Input,
  Rate,
  Row,
  Slider,
  Tag,
} from "antd";

// utils
import { DEFAULT_QUESTION_TYPES } from "../constant";
import {
  DeleteOutlined,
  FrownOutlined,
  MehOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import { colorPalette } from "../theme";
import { getTagColorStyles } from "../utils";

const QuestionContainer = styled.div`
  margin: 20px 0px;
`;

const QuestionTitleWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const Number = styled.div`
  width: 26px;
  height: 26px;
  border: 1px solid rgb(52, 71, 103);
  border-radius: 50%;
  text-align: center;
  line-height: 24px;
  font-size: 18px;
  color: rgb(52, 71, 103);
  margin-right: 10px;
`;

const QuestionTitle = styled.h3`
  font-weight: 400;
  font-size: 18px;
  line-height: 22px;
  color: rgb(52, 71, 103);
  margin: 0px;

  @media (max-width: 768px) {
    margin-bottom: 10px;
  }
`;

const AttributesWrapper = styled.div`
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const StyledRow = styled(Row)`
  .ant-rate {
    font-size: 42px;

    .ant-rate-star-zero {
      svg {
        fill: #ccc;
      }
    }
  }

  .ant-slider-mark-text {
    width: 3%;
  }

  .ant-slider-track {
    background-color: ${colorPalette.primary[600]} !important;
  }

  .ant-slider-rail {
    background-color: ${colorPalette.primary[300]} !important;
  }

  .ant-slider-handle::after {
    box-shadow: 0 0 0 2px ${colorPalette.primary[600]} !important;
  }

  .ant-slider-mark-text:first-child {
    margin-left: 5px;
  }
  .ant-slider-mark-text:last-child {
    margin-left: -40px;
  }

  @media (max-width: 479px) {
    .ant-rate {
      font-size: 55px;
    }
  }
`;

const InputContainer = styled(Flex)`
  margin-bottom: 10px;

  .ant-input {
    margin-bottom: 0px !important;
  }

  svg {
    font-size: 20px;
    color: ${colorPalette.danger[500]};
    cursor: pointer;
  }
`;

const deepCloneQuestion = (question) => {
  return {
    ...question,
    choices: question.choices
      ? question.choices
          .map((choice) => ({ ...choice }))
          .filter((ele) => ele?.text)
      : undefined,
  };
};

const Questions = ({
  question: element,
  index,
  onUpdateQuestion,
  onDelete,
  onRegenerate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [question, setQuestion] = useState({});
  const [originalQuestion, setOriginalQuestion] = useState({});

  useEffect(() => {
    setQuestion(deepCloneQuestion(element));
    setOriginalQuestion(deepCloneQuestion(element));
  }, [element]);

  const handleEdit = () => {
    // Save the current state before editing
    setOriginalQuestion(deepCloneQuestion(question));
    setIsEditing(true);
  };

  const handleCancel = () => {
    // Restore the original state
    setQuestion(deepCloneQuestion(originalQuestion));
    setIsEditing(false);
  };

  const handleSave = () => {
    onUpdateQuestion(deepCloneQuestion(question));
    setOriginalQuestion(deepCloneQuestion(question));
    setIsEditing(false);
  };

  const getOptionsText = (marks, value) => {
    // const optionsText = {
    //   1: "Strongly Disagree",
    //   2: "Disagree",
    //   3: "Neutral",
    //   4: "Agree",
    //   5: "Strongly Agree",
    // };
    return marks[value] || "";
  };

  const questionChoices = useMemo(() => {
    const customIcons = {
      1: <FrownOutlined />,
      2: <FrownOutlined />,
      3: <MehOutlined />,
      4: <SmileOutlined />,
      5: <SmileOutlined />,
    };

    if (isEditing) {
      if (
        [
          DEFAULT_QUESTION_TYPES.CHOICE,
          DEFAULT_QUESTION_TYPES.MULTIPLE_CHOICE,
        ].includes(question.type)
      ) {
        const { choices } = question;
        return (
          <>
            {choices.map((choice, index) => (
              <InputContainer key={choice.id} align="center" gap={10}>
                <Input
                  defaultValue={choice.text}
                  variant="underlined"
                  onChange={(e) => {
                    // Create new array with updated choice
                    const newChoices = [...choices];
                    newChoices[index] = {
                      ...newChoices[index],
                      text: e.target.value,
                    };
                    setQuestion({ ...question, choices: newChoices });
                  }}
                />
                <DeleteOutlined
                  onClick={() => {
                    // Create new array without the deleted choice
                    const newChoices = choices.filter((_, i) => i !== index);
                    setQuestion({ ...question, choices: newChoices });
                  }}
                />
              </InputContainer>
            ))}
            <Button
              type="primary"
              ghost
              style={{ marginTop: 10, width: "20%" }}
              onClick={() => {
                // Create new array with added choice
                const newChoices = [...choices, { id: Date.now(), text: "" }];
                setQuestion({ ...question, choices: newChoices });
              }}
            >
              Add Choice
            </Button>
          </>
        );
      }

      return null;
    }

    const marks = (question.choices || [])
      .map((ele) => ele?.text)
      .reduce((acc, choice, index) => {
        acc[index + 1] = choice;
        return acc;
      }, {});

    const sliderOptions = {};

    if (
      [
        DEFAULT_QUESTION_TYPES.SLIDER,
        DEFAULT_QUESTION_TYPES.SLIDER_RATING,
        DEFAULT_QUESTION_TYPES.SLIDER_AGREE_DISAGREE_RATING,
      ].includes(question.type) &&
      marks?.length
    ) {
      sliderOptions["tooltip"] = {
        formatter: (value) => (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            {marks[value] || ""}
          </span>
        ),
      };
    }

    switch (question.type) {
      case DEFAULT_QUESTION_TYPES.CHOICE:
      case DEFAULT_QUESTION_TYPES.MULTIPLE_CHOICE:
        return (
          <>
            <StyledRow gutter={10}>
              {question.choices.map((choice) => (
                <Col md={24} lg={12} xs={24} key={choice?.id}>
                  <Button
                    block
                    type="primary"
                    ghost
                    style={{ marginBottom: 10 }}
                  >
                    {choice?.text}
                  </Button>
                </Col>
              ))}
            </StyledRow>
          </>
        );
      case DEFAULT_QUESTION_TYPES.RATING:
      case DEFAULT_QUESTION_TYPES.EMOJI:
        return (
          <StyledRow>
            <Col span={24}>
              <Rate character={({ index = 0 }) => customIcons[index + 1]} />
            </Col>
          </StyledRow>
        );
      case DEFAULT_QUESTION_TYPES.SLIDER:
        return (
          <StyledRow>
            <Col span={24}>
              <Slider
                defaultValue={1}
                min={1}
                max={10}
                marks={marks}
                {...sliderOptions}
              />
            </Col>
          </StyledRow>
        );
      case DEFAULT_QUESTION_TYPES.SLIDER_RATING:
      case DEFAULT_QUESTION_TYPES.SLIDER_AGREE_DISAGREE_RATING:
        return (
          <StyledRow>
            <Col span={24}>
              <Slider
                defaultValue={1}
                min={1}
                max={5}
                marks={marks}
                {...sliderOptions}
              />
            </Col>
          </StyledRow>
        );
      case DEFAULT_QUESTION_TYPES.INPUT:
        return (
          <Input.TextArea rows={4} disabled placeholder="Your answer here..." />
        );
      default:
        return null;
    }
  }, [question, element, isEditing]);

  if (!question) {
    return null;
  }

  return (
    <>
      <QuestionContainer key={question.id}>
        <QuestionTitleWrapper gap={10}>
          <div>
            <Number>{index + 1}</Number>
          </div>
          {isEditing ? (
            <Input
              variant="underlined"
              autoFocus
              defaultValue={question.text}
              onChange={(e) =>
                setQuestion({ ...question, text: e.target.value })
              }
            />
          ) : (
            <>
              <QuestionTitle
                dangerouslySetInnerHTML={{
                  __html: question.text,
                }}
              />
            </>
          )}
        </QuestionTitleWrapper>
        {!isEditing && (
          <AttributesWrapper>
            {(question?.attributes || []).map((ele) => (
              <Tag
                key={ele.id}
                style={{
                  ...getTagColorStyles(ele.text),
                  marginBottom: "2px",
                }}
              >
                {ele.text}
              </Tag>
            ))}
            {(question?.admin_attributes || []).map((ele) => (
              <Tag
                key={ele}
                color="blue"
                style={{ border: 0, marginBottom: "2px" }}
              >
                {ele}
              </Tag>
            ))}
          </AttributesWrapper>
        )}
        {questionChoices}
        <Divider />
        {isEditing ? (
          <Flex gap={10} justify="end">
            <Button
              type="primary"
              ghost
              style={{ width: "25%" }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              style={{ width: "25%" }}
              onClick={handleSave}
            >
              Save
            </Button>
          </Flex>
        ) : (
          <Flex gap={10}>
            <Button
              danger
              block
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              Delete
            </Button>
            <Button color="primary" variant="dashed" block onClick={handleEdit}>
              Edit
            </Button>
            <Button
              ghost
              type="primary"
              block
              onClick={(e) => {
                e.stopPropagation();
                onRegenerate();
              }}
            >
              Regenerate
            </Button>
          </Flex>
        )}
        <Divider />
      </QuestionContainer>
    </>
  );
};
export default Questions;
